// Audio utility functions for playing notification sounds

class AudioManager {
  private static instance: AudioManager;
  private audioCache: Map<string, HTMLAudioElement> = new Map();
  private audioEnabled: boolean = false;

  private constructor() {
    // Enable audio after first user interaction
    this.enableAudioOnInteraction();
  }

  public static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  private getAudio(soundPath: string): HTMLAudioElement {
    if (!this.audioCache.has(soundPath)) {
      const audio = new Audio(soundPath);
      audio.preload = 'auto';
      this.audioCache.set(soundPath, audio);
    }
    return this.audioCache.get(soundPath)!;
  }

  private enableAudioOnInteraction(): void {
    const enableAudio = () => {
      this.audioEnabled = true;
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };

    document.addEventListener('click', enableAudio);
    document.addEventListener('keydown', enableAudio);
    document.addEventListener('touchstart', enableAudio);
  }

  public async playNotificationSound(): Promise<void> {
    if (!this.audioEnabled) {
      console.log('Audio not enabled yet - waiting for user interaction');
      return;
    }

    try {
      // Try to play the notification sound
      const audio = this.getAudio('/sounds/notification.mp3');
      audio.currentTime = 0; // Reset to beginning
      console.info('Playing notification sound:', audio.src);
      await audio.play();
    } catch (error) {
      console.warn('Could not play notification sound:', error);
      // Fallback: try to use Web Audio API to generate a simple beep
      try {
        console.info('Falling back to system beep');
        await this.playSystemBeep();
      } catch (beepError) {
        console.warn('Could not play system beep:', beepError);
        // Silent fail - no sound will be played
      }
    }
  }

  private async playSystemBeep(): Promise<void> {
    // Create a simple beep using Web Audio API as fallback
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800 Hz tone
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Low volume
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2); // 200ms beep

    return new Promise(resolve => {
      oscillator.onended = () => {
        audioContext.close();
        resolve();
      };
    });
  }

  public setVolume(volume: number): void {
    // Set volume for all cached audio elements (0.0 to 1.0)
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.audioCache.forEach(audio => {
      audio.volume = clampedVolume;
    });
  }
}

export const audioManager = AudioManager.getInstance();

// Convenience function
export const playNotificationSound = () => audioManager.playNotificationSound();
